import type { BadgeVariant } from '../../components/ui/Badge'

/**
 * Состояние черновика Wildberries для вкладки «Черновики PIM».
 *
 * Зачем отдельный модуль. До 10.09.2026 бейдж считался из `status` плюс одной
 * проверки «отправлено, ответа нет», и в это состояние сваливались две разные
 * вещи: карточка, которую ВБ принял и по которой мы просто ждём nmID, и
 * карточка, по которой площадка вернула ошибку. Оператор видел одинаковое
 * «Ответа нет» и в том, и в другом случае — а действия у них противоположные:
 * в первом надо подождать пять минут, во втором разбираться с причиной.
 *
 * ВБ и ОЗОН здесь намеренно разведены: у площадок разные механизмы получения
 * идентификатора и разные наборы ошибок. Этот модуль применяется только к
 * `marketplace === 'wb'`; путь ОЗОН остаётся на прежней логике без изменений.
 */

export interface DraftStateBadge {
  label: string
  variant: BadgeVariant
  /** Развёрнутое пояснение в подсказке: что произошло и что делать. */
  hint?: string
}

/** Поля карточки, по которым выводится состояние. Больше ничего не требуется. */
export interface WbDraftStateInput {
  status?: string | null
  submit_state?: string | null
  external_id?: string | null
  error?: string | null
  last_error_code?: string | null
}

export const WB_AWAITING_NMID_HINT = 'Выгрузка принята Wildberries, ошибок площадка'
  + ' не вернула. Номенклатурный номер (nmID) присваивается асинхронно: он появится'
  + ' после ближайшей синхронизации каталога, обычно в течение пяти минут. Ничего'
  + ' делать не нужно.'

export const WB_OUTCOME_UNKNOWN_HINT = 'Запрос ушёл, но ответа площадки не было —'
  + ' неизвестно, приняла ли она карточку. Повтор безопасен: дубля не будет,'
  + ' Wildberries отбивает второй товар с тем же артикулом.'

/**
 * Коды отказов, которые формирует сам ПИМ до или вместо обращения к площадке.
 *
 * Текст берётся коротким: он живёт в бейдже шириной в колонку. Причина целиком
 * уходит в подсказку, поэтому здесь важна различимость, а не полнота.
 */
const CODE_LABELS: Record<string, { label: string; variant: BadgeVariant; hint: string }> = {
  SEO_REVISION_MISSING: {
    label: 'Нет текста карточки',
    variant: 'red',
    hint: 'Замороженная ревизия текста недоступна: SEO-сервис её не отдал либо не хватило прав.'
      + ' Проверьте, не удалён ли черновик контента.',
  },
  SEO_REVISION_MISMATCH: {
    label: 'Текст пересобран',
    variant: 'red',
    hint: 'Замороженная ссылка указывает не на ту ревизию: после заморозки контент пересобрали.'
      + ' Карточку нужно заново подготовить к публикации.',
  },
  SEO_SNAPSHOT_INCOMPLETE: {
    label: 'Неполный снимок',
    variant: 'red',
    hint: 'В замороженном снимке карточки нет обязательных разделов. Подготовьте карточку заново.',
  },
  SEO_TEXT_MISSING: {
    label: 'Нет наименования или описания',
    variant: 'red',
    hint: 'Оба поля обязательны для площадки. Заполните их в SEO и подготовьте карточку заново.',
  },
  SEO_PACKAGE_MISSING: {
    label: 'Нет комплекта текста',
    variant: 'red',
    hint: 'Для карточки не собран комплект SEO-полей.',
  },
  PIPELINE_VISUALS_MISSING: {
    label: 'Нет визуалов',
    variant: 'red',
    hint: 'В замороженном манифесте карточки нет изображений. Без них площадка карточку не примет.',
  },
  PIPELINE_VISUALS_NOT_PUBLISHED: {
    label: 'Визуалы не опубликованы',
    variant: 'red',
    hint: 'Изображения есть, но не выложены на публичный адрес — площадке их скачать неоткуда.',
  },
  PIPELINE_VISUALS_CABINET_MISSING: {
    label: 'Нет визуалов кабинета',
    variant: 'red',
    hint: 'Для этого кабинета изображения не подготовлены.',
  },
  COMMERCIALS_PRICE_MISSING: {
    label: 'Не задана цена',
    variant: 'red',
    hint: 'У категории товара не заполнена цена для площадки — без неё карточку принимать некуда.',
  },
  MARKETPLACE_PRICE_MISSING: {
    label: 'Не задана цена',
    variant: 'red',
    hint: 'У категории товара не заполнена цена для площадки — без неё карточку принимать некуда.',
  },
  COMMERCIALS_PRICE_UPLOAD_FAILED: {
    label: 'Цена не ушла',
    variant: 'red',
    hint: 'Карточка на площадке есть, но отдельный вызов установки цены не прошёл. Повторите выгрузку.',
  },
  MARKETPLACE_ARTICLE_TAKEN: {
    label: 'Артикул занят',
    variant: 'yellow',
    hint: 'Артикул уже используется на площадке другой карточкой. Дубль создан не будет.',
  },
  MARKETPLACE_CARD_REJECTED: {
    label: 'Отклонена площадкой',
    variant: 'red',
    hint: 'Wildberries отклонил карточку и назвал причину — она в тексте ошибки ниже.',
  },
  MARKETPLACE_CATALOG_STALE: {
    label: 'Каталог кабинета устарел',
    variant: 'yellow',
    hint: 'Снимок каталога старше допустимого, поэтому барьер артикула не пропустил отправку.'
      + ' Повторите после синхронизации каталога.',
  },
  MARKETPLACE_PUBLISH_IN_PROGRESS: {
    label: 'Уже отправляется',
    variant: 'yellow',
    hint: 'Карточку в этот момент выгружает другой запрос. Повторная отправка заблокирована намеренно.',
  },
  MARKETPLACE_PUBLISH_OUTCOME_UNKNOWN: {
    label: 'Исход неизвестен',
    variant: 'yellow',
    hint: WB_OUTCOME_UNKNOWN_HINT,
  },
  MARKETPLACE_COMMERCIALS_OUTCOME_UNKNOWN: {
    label: 'Цена: исход неизвестен',
    variant: 'yellow',
    hint: 'Запрос установки цены ушёл, ответа не было. Повторите выгрузку.',
  },
  MARKETPLACE_SELLER_DRY_RUN: {
    label: 'Сухой прогон',
    variant: 'gray',
    hint: 'Кабинет работает в режиме сухого прогона: наружу ничего не отправлялось.',
  },
  MARKETPLACE_PUBLISH_MODE: {
    label: 'Сухой прогон',
    variant: 'gray',
    hint: 'Выгрузка идёт в режиме dry_run: наружу ничего не отправлялось.',
  },
  MARKETPLACE_PUBLISH_KILL_SWITCH: {
    label: 'Выгрузка выключена',
    variant: 'gray',
    hint: 'Публикация остановлена рубильником. Обратитесь к администратору.',
  },
  WB_CONFIRM_ABANDONED: {
    label: 'Не подтверждена в срок',
    variant: 'red',
    hint: 'Отправка была, но за отведённый срок площадка так и не показала карточку в каталоге.'
      + ' Требуется ручная проверка кабинета.',
  },
  WB_CARDS_MERGE_FAILED: {
    label: 'Склейка не удалась',
    variant: 'red',
    hint: 'Карточка создана, но не присоединилась к склейке: не удалось прочитать nmID или imtID.',
  },
  WB_CARD_MERGE_FAILED: {
    label: 'Склейка не удалась',
    variant: 'red',
    hint: 'Карточка создана, но не присоединилась к склейке: не удалось прочитать nmID или imtID.',
  },
  WB_TRANSPORT_UNAVAILABLE: {
    label: 'Сервис ВБ недоступен',
    variant: 'yellow',
    hint: 'Транспортный сервис Wildberries не ответил. Отправка не состоялась, повтор безопасен.',
  },
  WB_REQUEST_CANCELLED: {
    label: 'Отправка отменена',
    variant: 'gray',
    hint: 'Запрос отменён до обращения к площадке — наружу ничего не ушло.',
  },
  WB_NO_RESULT: {
    label: 'Пустой ответ ВБ',
    variant: 'yellow',
    hint: 'Площадка ответила, но не вернула результат по этому артикулу.',
  },
}

/** Ответы транспорта, по которым неизвестно, приняла ли площадка запрос. */
const UNKNOWN_OUTCOME_MARKERS = [
  'ETIMEDOUT',
  'ECONNRESET',
  'ECONNREFUSED',
  'ENOTFOUND',
  'EAI_AGAIN',
  'UND_ERR_',
  'Request timeout',
  'socket hang up',
]

/**
 * Код ответа площадки из текста ошибки.
 *
 * Транспорт формулирует по-разному: `WB API error (429) after 3 retry attempts`,
 * `WB API error (500) on retry: …`, `Wildberries API request failed: 429 Too Many
 * Requests - …`. Берём первое трёхзначное число, стоящее как HTTP-статус.
 */
export const wbHttpStatusFromError = (text: string): number | null => {
  const parenthesised = /\((\d{3})\)/.exec(text)
  if (parenthesised) return Number(parenthesised[1])
  const plain = /(?:failed|error|статус)\D{0,12}?\b([1-5]\d{2})\b/i.exec(text)
  return plain ? Number(plain[1]) : null
}

const httpBadge = (status: number, text: string): DraftStateBadge | null => {
  if (status === 429) {
    return {
      label: '429 — лимит запросов ВБ',
      variant: 'yellow',
      hint: 'Wildberries считает лимит на всю категорию «Контент» — 100 запросов в минуту на кабинет,'
        + ' и его делят создание карточек, загрузка фото и обход каталога. Карточка не создана,'
        + ' повтор безопасен.',
    }
  }
  if (status === 500 || status === 502 || status === 503 || status === 504) {
    return {
      label: `${status} — сервис ВБ недоступен`,
      variant: 'yellow',
      hint: 'Сбой на стороне площадки. Принял ли Wildberries запрос — неизвестно, но повтор безопасен:'
        + ' дубля не будет, второй товар с тем же артикулом площадка отбивает.',
    }
  }
  if (status === 400) {
    // Суточный лимит создания карточек ВБ тоже приходит как 400. 28.10.2026 под
    // «не принял состав» ушла пачка кабинета СВВ, хотя карточки были в порядке
    // (BUG-000006).
    if (/daily limit|used up your daily|суточн\w* лимит/i.test(text)) {
      return {
        label: '400 — суточный лимит ВБ',
        variant: 'yellow',
        hint: 'Wildberries разрешает создать не больше 5000 карточек в сутки на кабинет, и лимит исчерпан.'
          + ' С карточкой всё в порядке: она не создана, повторите выгрузку на следующий день.',
      }
    }
    // ВБ отвечает 400 и на собственные внутренние сбои — тогда это не состав
    // карточки, и разбирать данные бесполезно.
    const platformGlitch = /internal server error|checkbanbynmids/i.test(text)
    return platformGlitch
      ? {
        label: '400 — сбой на стороне ВБ',
        variant: 'yellow',
        hint: 'Площадка вернула 400 с внутренней ошибкой. Это сбой Wildberries, а не состава карточки —'
          + ' повторите выгрузку позже.',
      }
      : {
        label: '400 — ВБ не принял состав',
        variant: 'red',
        hint: 'Площадка отклонила содержимое карточки. Причина в тексте ошибки ниже.',
      }
  }
  if (status === 401 || status === 403) {
    return {
      label: `${status} — ключ кабинета не принят`,
      variant: 'red',
      hint: 'Wildberries отказал в доступе. Проверьте токен кабинета и срок его действия.',
    }
  }
  if (status === 404) {
    return {
      label: '404 — метод ВБ не найден',
      variant: 'red',
      hint: 'Площадка не знает вызванный метод. Скорее всего изменился API Wildberries.',
    }
  }
  if (status === 409) {
    return {
      label: '409 — артикул занят',
      variant: 'yellow',
      hint: 'Артикул уже используется на площадке. Дубль создан не будет.',
    }
  }
  return null
}

/**
 * Человеческая формулировка отказа по коду и тексту ошибки.
 *
 * Порядок разбора значим: сначала собственный код ПИМа (он точнее всего
 * называет причину), затем ответ площадки по HTTP-статусу, затем признаки
 * неизвестного исхода. Нераспознанное показываем как «Ошибка» — врать
 * конкретикой хуже, чем честно признать незнакомый случай.
 */
export const wbErrorBadge = (
  code?: string | null,
  error?: string | null,
): DraftStateBadge => {
  const text = String(error ?? '').trim()
  const normalizedCode = String(code ?? '').trim().toUpperCase()
    || /^([A-Z][A-Z0-9_]*):/.exec(text)?.[1]
    || ''

  const known = CODE_LABELS[normalizedCode]
  if (known) {
    return { label: known.label, variant: known.variant, hint: text ? `${known.hint}\n\n${text}` : known.hint }
  }

  const status = wbHttpStatusFromError(text)
  if (status !== null) {
    const badge = httpBadge(status, text)
    if (badge) return { ...badge, hint: text ? `${badge.hint}\n\n${text}` : badge.hint }
  }

  if (UNKNOWN_OUTCOME_MARKERS.some(marker => text.toUpperCase().includes(marker.toUpperCase()))) {
    return {
      label: 'Нет ответа ВБ',
      variant: 'yellow',
      hint: `${WB_OUTCOME_UNKNOWN_HINT}\n\n${text}`,
    }
  }

  return {
    label: 'Ошибка',
    variant: 'red',
    hint: text || 'Причина не сохранена.',
  }
}

/** Есть ли по карточке пойманная ошибка — по коду либо по тексту. */
const hasError = (card: WbDraftStateInput): boolean =>
  Boolean(String(card.last_error_code ?? '').trim() || String(card.error ?? '').trim())

/**
 * Состояние черновика ВБ.
 *
 * Разбор идёт от самого достоверного признака к самому общему:
 *   1. внешний идентификатор есть — карточка на площадке, показываем статус;
 *   2. запрос в полёте;
 *   3. площадка отказала — называем причину;
 *   4. запрос ушёл: ошибки нет — ждём nmID, ошибка есть — называем её;
 *   5. до отправки дело не дошло — обычный статус карточки.
 */
export const wbDraftStateBadge = (
  card: WbDraftStateInput,
  statusInfo: Record<string, { label: string; variant: BadgeVariant }>,
): DraftStateBadge => {
  const submitState = String(card.submit_state ?? '').trim().toLowerCase()
  const published = String(card.external_id ?? '').trim()
  const fallback = statusInfo[String(card.status ?? '')]
    || { label: String(card.status ?? ''), variant: 'gray' as BadgeVariant }

  if (published) return fallback
  if (submitState === 'in_flight') {
    return {
      label: 'Отправляется',
      variant: 'yellow',
      hint: 'Запрос к Wildberries выполняется прямо сейчас.',
    }
  }
  if (submitState === 'rejected') return wbErrorBadge(card.last_error_code, card.error)
  if (submitState === 'sent') {
    return hasError(card)
      ? wbErrorBadge(card.last_error_code, card.error)
      : { label: 'Ожидание nmID', variant: 'blue', hint: WB_AWAITING_NMID_HINT }
  }
  if (card.status === 'error') return wbErrorBadge(card.last_error_code, card.error)
  return fallback
}
